import {
    PLAYER_HEIGHT_CROUCHING,
    PLAYER_HEIGHT_STANDING,
    PlayerState,
    TileType,
    isBodyPassable,
    isPassable,
    isInSpawnProtection,
    isEquipmentItem,
    isToolItem,
    isDiggable,
    isFruitTile,
    isBoneLikeBlock,
    isIndestructible,
    isShrubTile,
    BASKET_CAPACITY,
    type ToolActionType,
    isHealingFruit,
    getFruitHealAmount,
    KILI_KULUPU_HEAL_TOTAL,
    KILI_NIGHTSHADE_DAMAGE,
} from '@blockworld/shared';

export interface GameSceneInteractionContext {
    [key: string]: any;
}

/**
 * Extracted from GameScene down-action methods as a move-only refactor.
 */
export function handleDownAction(scene: GameSceneInteractionContext, player: any): void {
    // No block actions in spawn protection zone
    if (isInSpawnProtection(player.x, player.y, scene.roomBoxes)) {
        // Still allow crouching
        if (player.state !== PlayerState.FALLING && scene.isOnGround(player) && !player.isCrouching) {
            if (!scene.isOnOrInLadder(player)) {
                player.y += 1;
                player.isCrouching = true;
                player.state = PlayerState.CROUCHING;
            }
        }
        return;
    }

    // If falling, queue block action for global tick to sync with block falls
    if (player.state === PlayerState.FALLING) {
        const frontX = player.x + player.facing;
        const playerHeight = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        const feetY = player.y + playerHeight - 1;

        // Capture position at input time
        player.pendingActionX = frontX;
        player.pendingActionY = feetY;

        if (player.isHoldingBlock && player.heldBlockType !== null) {
            player.pendingBlockPlace = true;
        } else {
            player.pendingBlockPickup = true;
        }
        return;
    }

    // Shovel: down key with empty overhead digs block below
    if (player.equippedItem === TileType.ILO_ANPA && !player.isHoldingBlock) {
        const playerHeight = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        const feetY = player.y + playerHeight - 1;
        const belowY = feetY + 1;
        const belowTile = scene.getTile(player.x, belowY);
        if (isDiggable(belowTile)) {
            scene.setLocalTile(player.x, belowY, TileType.SKY);
            player.isHoldingBlock = true;
            player.heldBlockType = belowTile;
            scene.networkClient.sendToolAction('shovel_dig', player.x, player.y, player.facing, player.isCrouching);
            return;
        }
    }

    if (!executeDownAction(scene, player)) {
        // Crouch fallback (only when no block interaction occurred)
        if (scene.isOnGround(player) && !player.isCrouching) {
            // Don't crouch on a ladder — DOWN means descend, not crouch
            if (scene.isOnOrInLadder(player)) return;
            player.y += 1;
            player.isCrouching = true;
            player.state = PlayerState.CROUCHING;
        }
    }
}

// Execute the actual down action (place/pickup)
// Called directly when grounded or for immediate actions
// If useStoredPos is true, uses pendingActionX/Y instead of calculating from current position
// Returns true if a block interaction was performed, false otherwise
export function executeDownAction(scene: GameSceneInteractionContext, player: any, useStoredPos: boolean = false): boolean {
    let frontX: number;
    let feetY: number;

    if (useStoredPos) {
        frontX = player.pendingActionX;
        feetY = player.pendingActionY;
    } else {
        frontX = player.x + player.facing;
        const playerHeight = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        feetY = player.y + playerHeight - 1;
    }

    const frontBottomTile = scene.getTile(frontX, feetY);
    const frontTopTile = scene.getTile(frontX, feetY - 1);

    if (player.isHoldingBlock && player.heldBlockType !== null) {
        // STICK-on-STICK combination: delete the front stick, convert held to LADDER
        if (player.heldBlockType === TileType.STICK) {
            let combineY: number | null = null;
            if (frontBottomTile === TileType.STICK) {
                combineY = feetY;
            } else if (frontTopTile === TileType.STICK) {
                combineY = feetY - 1;
            }
            if (combineY !== null) {
                scene.setLocalTile(frontX, combineY, TileType.SKY);
                scene.localFallingBlocks.delete(`${frontX},${combineY}`);
                // DON'T propagate instability here - wait for server echo
                // This matches placement behavior and keeps falling synced
                player.heldBlockType = TileType.LADDER;
                // Send combine: still holding, now LADDER
                scene.networkClient.sendBlockAction(
                    'combine', frontX, combineY, TileType.STICK,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, true, TileType.LADDER
                );
                return true;
            }
        }

        // SKULL/BONE + SAPLING = tree: consume bone-like block, server spawns tree
        if (isBoneLikeBlock(player.heldBlockType)) {
            let saplingY: number | null = null;
            if (frontBottomTile === TileType.SAPLING) {
                saplingY = feetY;
            } else if (frontTopTile === TileType.SAPLING) {
                saplingY = feetY - 1;
            }
            if (saplingY !== null) {
                scene.setLocalTile(frontX, saplingY, TileType.SKY);
                scene.networkClient.sendBlockAction(
                    'place', frontX, saplingY, player.heldBlockType,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, false, -1
                );
                player.isHoldingBlock = false;
                player.heldBlockType = null;
                return true;
            }

            // BONE-LIKE + STICK on GRASS = sapling: consume bone-like, convert stick
            let stickY: number | null = null;
            if (frontBottomTile === TileType.STICK && scene.getTile(frontX, feetY + 1) === TileType.GRASS) {
                stickY = feetY;
            } else if (frontTopTile === TileType.STICK && scene.getTile(frontX, feetY) === TileType.GRASS) {
                stickY = feetY - 1;
            }
            if (stickY !== null) {
                scene.setLocalTile(frontX, stickY, TileType.SAPLING);
                scene.localFallingBlocks.delete(`${frontX},${stickY}`);
                scene.networkClient.sendBlockAction(
                    'place', frontX, stickY, player.heldBlockType,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, false, -1
                );
                player.isHoldingBlock = false;
                player.heldBlockType = null;
                return true;
            }
        }

        // KILI_MA (potato) + DIRT → KASI_MA: plant potato into dirt
        if (player.heldBlockType === TileType.KILI_MA) {
            let dirtY: number | null = null;
            if (frontBottomTile === TileType.DIRT) {
                dirtY = feetY;
            } else if (frontTopTile === TileType.DIRT) {
                dirtY = feetY - 1;
            }
            if (dirtY !== null) {
                scene.setLocalTile(frontX, dirtY, TileType.KASI_MA);
                scene.localFallingBlocks.delete(`${frontX},${dirtY}`);
                scene.networkClient.sendBlockAction(
                    'place', frontX, dirtY, TileType.KILI_MA,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, false, -1
                );
                player.isHoldingBlock = false;
                player.heldBlockType = null;
                return true;
            }
        }

        // PLANK gap-bridging: when both front tiles are SKY, player is on solid ground,
        // and the tile diagonally in front+below is also SKY, place the plank 1 tile lower
        if (player.heldBlockType === TileType.PLANK
            && frontBottomTile === TileType.SKY && frontTopTile === TileType.SKY
            && !isPassable(scene.getTile(player.x, feetY + 1))
            && scene.getTile(frontX, feetY + 1) === TileType.SKY) {
            const placeY = feetY + 1;
            scene.setLocalTile(frontX, placeY, TileType.PLANK);
            scene.networkClient.sendBlockAction(
                'place', frontX, placeY, TileType.PLANK,
                player.x, player.y, player.state, player.facing,
                player.isCrouching, false,
                -1
            );
            player.isHoldingBlock = false;
            player.heldBlockType = null;
            return true;
        }

        // Note: Can only place on SKY tiles, not PORTAL or LADDER
        // Exception: LADDER can be placed below an existing LADDER (extending downward)
        // This matches server validation (GameRoom) and shared physics (handleDownAction)
        if (frontBottomTile === TileType.SKY && (frontTopTile === TileType.SKY || (player.heldBlockType === TileType.LADDER && frontTopTile === TileType.LADDER))) {
            scene.setLocalTile(frontX, feetY, player.heldBlockType);
            // Predict falling locally so block falls in sync with player
            if (scene.isFallingType(player.heldBlockType!)) {
                const key = `${frontX},${feetY}`;
                scene.localFallingBlocks.add(key);
                if (player.heldBlockType === TileType.LOG) {
                    scene.logInertia.set(key, player.facing);
                }
            }
            // Send NEW state: not holding after place
            scene.networkClient.sendBlockAction(
                'place', frontX, feetY, player.heldBlockType,
                player.x, player.y, player.state, player.facing,
                player.isCrouching, false, // NEW state: not holding
                -1 // NEW state: no held block type
            );
            player.isHoldingBlock = false;
            player.heldBlockType = null;
            return true;
        } else if (frontTopTile === TileType.SKY && frontBottomTile !== TileType.SKY && !isPassable(frontBottomTile)) {
            scene.setLocalTile(frontX, feetY - 1, player.heldBlockType);
            // Predict falling locally so block falls in sync with player
            if (scene.isFallingType(player.heldBlockType!)) {
                const key = `${frontX},${feetY - 1}`;
                scene.localFallingBlocks.add(key);
                if (player.heldBlockType === TileType.LOG) {
                    scene.logInertia.set(key, player.facing);
                }
            }
            // Send NEW state: not holding after place
            scene.networkClient.sendBlockAction(
                'place', frontX, feetY - 1, player.heldBlockType,
                player.x, player.y, player.state, player.facing,
                player.isCrouching, false, // NEW state: not holding
                -1 // NEW state: no held block type
            );
            player.isHoldingBlock = false;
            player.heldBlockType = null;
            return true;
        } else if (player.heldBlockType === TileType.LADDER && frontBottomTile === TileType.LADDER && frontTopTile === TileType.LADDER) {
            // Ladder extension: both front tiles are ladder, seek up to place at top
            let scanY = feetY - 2;
            while (scene.getTile(frontX, scanY) === TileType.LADDER) {
                scanY--;
            }
            if (scene.getTile(frontX, scanY) === TileType.SKY) {
                scene.setLocalTile(frontX, scanY, TileType.LADDER);
                scene.networkClient.sendBlockAction(
                    'place', frontX, scanY, TileType.LADDER,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, false,
                    -1
                );
                player.isHoldingBlock = false;
                player.heldBlockType = null;
                return true;
            }
        }
    } else {
        // KASI_ALE: indestructible — cannot be picked up
        if (isIndestructible(frontBottomTile)) {
            return false;
        }

        // KASI_IJO (Shrub): disappears on pickup attempt (not held)
        if (isShrubTile(frontBottomTile)) {
            scene.setLocalTile(frontX, feetY, TileType.SKY);
            scene.networkClient.sendBlockAction(
                'pickup', frontX, feetY, TileType.KASI_IJO,
                player.x, player.y, player.state, player.facing,
                player.isCrouching, false, -1
            );
            return true;
        }

        // KASI_MA (Potato in Dirt): separate into dirt + potato in hand
        if (frontBottomTile === TileType.KASI_MA && isBodyPassable(frontTopTile)) {
            scene.setLocalTile(frontX, feetY, TileType.DIRT);
            // Dirt is a falling type, predict
            scene.localFallingBlocks.add(`${frontX},${feetY}`);
            scene.networkClient.sendBlockAction(
                'pickup', frontX, feetY, TileType.KASI_MA,
                player.x, player.y, player.state, player.facing,
                player.isCrouching, true, TileType.KILI_MA
            );
            player.isHoldingBlock = true;
            player.heldBlockType = TileType.KILI_MA;
            return true;
        }

        // Flint + STICK in hand → Club (ILO_IJO) crafting
        if (frontBottomTile === TileType.FLINT && player.isHoldingBlock && player.heldBlockType === TileType.STICK) {
            scene.setLocalTile(frontX, feetY, TileType.SKY);
            scene.localFallingBlocks.delete(`${frontX},${feetY}`);
            player.isHoldingBlock = false;
            player.heldBlockType = null;
            player.equippedItem = TileType.ILO_IJO;
            scene.networkClient.sendBlockAction(
                'pickup', frontX, feetY, TileType.FLINT,
                player.x, player.y, player.state, player.facing,
                player.isCrouching, true, TileType.STICK
            );
            scene.combatHUD?.updateEquipmentDisplay(player.equippedItem);
            return true;
        }

        // KIWEN_MI: unpickupable, sets spawn point on A-press or Down
        if (frontBottomTile === TileType.KIWEN_MI) {
            scene.networkClient.sendSetSpawnPoint(frontX, feetY);
            if (scene.localPlayer) {
                scene.localPlayer.spawnBlockX = frontX;
                scene.localPlayer.spawnBlockY = feetY - 1;
            }
            return true;
        }

        // KIWEN_NIMI: cycle sign glyph on interaction
        if (frontBottomTile === TileType.KIWEN_NIMI) {
            scene.networkClient.sendSignCycle(frontX, feetY);
            return true;
        }

        // KIWEN_JELO (Gold Block): pickupable, no gravity
        if (frontBottomTile === TileType.KIWEN_JELO && isBodyPassable(frontTopTile)) {
            scene.setLocalTile(frontX, feetY, TileType.SKY);
            scene.networkClient.sendBlockAction(
                'pickup', frontX, feetY, TileType.KIWEN_JELO,
                player.x, player.y, player.state, player.facing,
                player.isCrouching, true,
                TileType.KIWEN_JELO
            );
            player.isHoldingBlock = true;
            player.heldBlockType = TileType.KIWEN_JELO;
            return true;
        }

        // Heart container: consume and heal, or hold above head if full HP
        if (frontBottomTile === TileType.HEART_CONTAINER && isBodyPassable(frontTopTile)) {
            scene.setLocalTile(frontX, feetY, TileType.SKY);
            if (scene.localPlayer && scene.localPlayer.hp < scene.localPlayer.maxHp) {
                // Consume: heal 1
                scene.networkClient.sendBlockAction(
                    'pickup', frontX, feetY, TileType.HEART_CONTAINER,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, false, -1
                );
                scene.localPlayer.hp = Math.min(scene.localPlayer.hp + 1, scene.localPlayer.maxHp);
                scene.combatHUD.updateHeartDisplay(scene.localPlayer.hp, scene.localPlayer.maxHp);
            } else {
                // Full HP: hold above head
                scene.networkClient.sendBlockAction(
                    'pickup', frontX, feetY, TileType.HEART_CONTAINER,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, true, TileType.HEART_CONTAINER
                );
                player.isHoldingBlock = true;
                player.heldBlockType = TileType.HEART_CONTAINER;
            }
            return true;
        }
        // Healing fruits: consume and heal, or hold above head if full HP
        if (isHealingFruit(frontBottomTile) && frontBottomTile !== TileType.KILI_KULUPU && isBodyPassable(frontTopTile)) {
            scene.setLocalTile(frontX, feetY, TileType.SKY);
            if (scene.localPlayer && scene.localPlayer.hp < scene.localPlayer.maxHp) {
                const healAmount = getFruitHealAmount(frontBottomTile);
                scene.networkClient.sendBlockAction(
                    'pickup', frontX, feetY, frontBottomTile,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, false, -1
                );
                scene.localPlayer.hp = Math.min(scene.localPlayer.hp + healAmount, scene.localPlayer.maxHp);
                scene.combatHUD.updateHeartDisplay(scene.localPlayer.hp, scene.localPlayer.maxHp);
            } else {
                scene.networkClient.sendBlockAction(
                    'pickup', frontX, feetY, frontBottomTile,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, true, frontBottomTile
                );
                player.isHoldingBlock = true;
                player.heldBlockType = frontBottomTile;
            }
            return true;
        }
        // Grape cluster (KILI_KULUPU): start slow heal or hold if full
        if (frontBottomTile === TileType.KILI_KULUPU && isBodyPassable(frontTopTile)) {
            scene.setLocalTile(frontX, feetY, TileType.SKY);
            if (scene.localPlayer && scene.localPlayer.hp < scene.localPlayer.maxHp) {
                scene.networkClient.sendBlockAction(
                    'pickup', frontX, feetY, TileType.KILI_KULUPU,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, false, -1
                );
                // Slow heal handled by tickSlowHeal on server/local
            } else {
                scene.networkClient.sendBlockAction(
                    'pickup', frontX, feetY, TileType.KILI_KULUPU,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, true, TileType.KILI_KULUPU
                );
                player.isHoldingBlock = true;
                player.heldBlockType = TileType.KILI_KULUPU;
            }
            return true;
        }
        // Nightshade (KILI_IKE): always consume and damage
        if (frontBottomTile === TileType.KILI_IKE && isBodyPassable(frontTopTile)) {
            scene.setLocalTile(frontX, feetY, TileType.SKY);
            scene.networkClient.sendBlockAction(
                'pickup', frontX, feetY, TileType.KILI_IKE,
                player.x, player.y, player.state, player.facing,
                player.isCrouching, false, -1
            );
            if (scene.localPlayer) {
                scene.localPlayer.hp = Math.max(scene.localPlayer.hp - KILI_NIGHTSHADE_DAMAGE, 0);
                scene.combatHUD.updateHeartDisplay(scene.localPlayer.hp, scene.localPlayer.maxHp);
            }
            return true;
        }
        // Big heart container: consume, increase max HP, fully heal
        if (frontBottomTile === TileType.BIG_HEART_CONTAINER && isBodyPassable(frontTopTile)) {
            scene.setLocalTile(frontX, feetY, TileType.SKY);
            scene.networkClient.sendBlockAction(
                'pickup', frontX, feetY, TileType.BIG_HEART_CONTAINER,
                player.x, player.y, player.state, player.facing,
                player.isCrouching, false, -1
            );
            if (scene.localPlayer) {
                scene.localPlayer.maxHp += 1;
                scene.localPlayer.hp = scene.localPlayer.maxHp;
                scene.combatHUD.updateHeartDisplay(scene.localPlayer.hp, scene.localPlayer.maxHp);
            }
            return true;
        }
        // Equipment items: goes into equipment slot (not held above head)
        // If player already has a DIFFERENT equipment item, swap them.
        // If player has the SAME item, fall through to normal pickup (on head).
        if (isEquipmentItem(frontBottomTile) && isBodyPassable(frontTopTile)
            && frontBottomTile !== player.equippedItem) {
            const newItem = frontBottomTile;
            const oldItem = player.equippedItem;
            if (oldItem !== null) {
                // Swap: place old equipment where new one was
                scene.setLocalTile(frontX, feetY, oldItem);
                // Old item is a falling type, predict its fall
                const key = `${frontX},${feetY}`;
                scene.localFallingBlocks.add(key);
                scene.networkClient.sendBlockAction(
                    'swap', frontX, feetY, newItem,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, player.isHoldingBlock,
                    player.heldBlockType ?? -1
                );
            } else {
                // No existing equipment — just pick up into slot
                scene.setLocalTile(frontX, feetY, TileType.SKY);
                scene.localFallingBlocks.delete(`${frontX},${feetY}`);
                scene.networkClient.sendBlockAction(
                    'pickup', frontX, feetY, newItem,
                    player.x, player.y, player.state, player.facing,
                    player.isCrouching, player.isHoldingBlock,
                    player.heldBlockType ?? -1
                );
            }
            player.equippedItem = newItem;
            // Force release sword if we just swapped away from iron sword
            if (player.isHoldingSword && newItem !== TileType.IRON_SWORD) {
                player.isHoldingSword = false;
                scene.networkClient.sendSwordHold(false);
            }
            scene.combatHUD.updateEquipmentDisplay(player.equippedItem);
            return true;
        }
        // Can pick up falling-type blocks (dirt, grass, skull, stick, log)
        // Sticks can be picked up even under other blocks
        if (scene.isFallingType(frontBottomTile) && (frontBottomTile === TileType.STICK || isBodyPassable(frontTopTile))) {
            const pickedUpType = frontBottomTile;
            scene.setLocalTile(frontX, feetY, TileType.SKY);
            // Send NEW state: holding after pickup
            scene.networkClient.sendBlockAction(
                'pickup', frontX, feetY, pickedUpType,
                player.x, player.y, player.state, player.facing,
                player.isCrouching, true, // NEW state: holding
                pickedUpType // NEW state: held block type
            );
            // Remove from falling tracking if it was there
            scene.localFallingBlocks.delete(`${frontX},${feetY}`);
            // DON'T propagate instability here - wait for server echo
            // This matches placement behavior and keeps falling synced
            player.isHoldingBlock = true;
            player.heldBlockType = pickedUpType;
            return true;
        }
        // Planks: solid non-gravity block, pickupable like dirt
        if (frontBottomTile === TileType.PLANK && isBodyPassable(frontTopTile)) {
            scene.setLocalTile(frontX, feetY, TileType.SKY);
            scene.networkClient.sendBlockAction(
                'pickup', frontX, feetY, TileType.PLANK,
                player.x, player.y, player.state, player.facing,
                player.isCrouching, true,
                TileType.PLANK
            );
            player.isHoldingBlock = true;
            player.heldBlockType = TileType.PLANK;
            return true;
        }
    }

    return false;
}

/**
 * Handle spacebar/A-button block interaction.
 * Same behavior as Down key for block pickup/placement, but without crouch fallback.
 * Returns true if a block interaction was performed (suppresses sword).
 */
export function handleAttackBlockAction(scene: GameSceneInteractionContext, player: any): boolean {
    if (isInSpawnProtection(player.x, player.y, scene.roomBoxes)) return false;

    if (player.state === PlayerState.FALLING) {
        // Queue for global tick to sync with falling blocks
        player.pendingAttackBlock = true;
        return true;
    }

    return executeAttackBlockAction(scene, player);
}

/**
 * Execute spacebar/A-button block action.
 * If a tool item is equipped, execute the tool action instead of normal block logic.
 * Otherwise same as executeDownAction (pickup/place in front), without crouch fallback.
 */
export function executeAttackBlockAction(scene: GameSceneInteractionContext, player: any): boolean {
    // Tool items have custom A-button behavior
    if (isToolItem(player.equippedItem)) {
        return executeToolAction(scene, player);
    }
    return executeDownAction(scene, player);
}

/**
 * Execute a tool-specific action based on equipped tool.
 * Called when A-button is pressed with a tool equipped.
 */
function executeToolAction(scene: GameSceneInteractionContext, player: any): boolean {
    const playerHeight = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
    const feetY = player.y + playerHeight - 1;
    const frontX = player.x + player.facing;

    switch (player.equippedItem) {
        case TileType.ILO_ANPA: { // Shovel
            if (player.isHoldingBlock) {
                // Block on head: place it like normal
                return executeDownAction(scene, player);
            }
            // Dig block below feet onto head
            const belowY = feetY + 1;
            const belowTile = scene.getTile(player.x, belowY);
            if (isDiggable(belowTile)) {
                scene.setLocalTile(player.x, belowY, TileType.SKY);
                player.isHoldingBlock = true;
                player.heldBlockType = belowTile;
                scene.networkClient.sendToolAction('shovel_dig', player.x, player.y, player.facing, player.isCrouching);
                return true;
            }
            return false;
        }

        case TileType.ILO_LUPA: { // Drill
            const isDown = scene.inputState?.down;
            if (isDown) {
                // Drill below
                const belowY = feetY + 1;
                const belowTile = scene.getTile(player.x, belowY);
                if (belowTile !== TileType.SKY && belowTile !== TileType.PORTAL && belowTile !== TileType.WATER && belowTile !== TileType.WATER_SURFACE && belowTile !== TileType.RUNE) {
                    scene.setLocalTile(player.x, belowY, TileType.SKY);
                    scene.networkClient.sendToolAction('drill_below', player.x, player.y, player.facing, player.isCrouching);
                    return true;
                }
            } else {
                // Drill in front
                const frontTile = scene.getTile(frontX, feetY);
                if (frontTile !== TileType.SKY && frontTile !== TileType.PORTAL && frontTile !== TileType.WATER && frontTile !== TileType.WATER_SURFACE && frontTile !== TileType.RUNE) {
                    scene.setLocalTile(frontX, feetY, TileType.SKY);
                    scene.networkClient.sendToolAction('drill_front', player.x, player.y, player.facing, player.isCrouching);
                    return true;
                }
            }
            return false;
        }

        case TileType.ILO_JAKI: { // Plunger — pull from 2 tiles away
            // Only triggers on A press (not down)
            const pullFromX = player.x + player.facing * 2;
            const pullToX = frontX;
            const pullTile = scene.getTile(pullFromX, feetY);
            const destTile = scene.getTile(pullToX, feetY);
            if (pullTile !== TileType.SKY && pullTile !== TileType.PORTAL && pullTile !== TileType.WATER && pullTile !== TileType.WATER_SURFACE && pullTile !== TileType.RUNE
                && destTile === TileType.SKY) {
                scene.setLocalTile(pullFromX, feetY, TileType.SKY);
                scene.setLocalTile(pullToX, feetY, pullTile);
                scene.networkClient.sendToolAction('plunger_pull', player.x, player.y, player.facing, player.isCrouching);
                return true;
            }
            return false;
        }

        case TileType.ILO_POKI: { // Bucket
            if (player.bucketFull) {
                // Pour water in front
                const frontTile = scene.getTile(frontX, feetY);
                if (frontTile === TileType.SKY) {
                    scene.setLocalTile(frontX, feetY, TileType.WATER);
                    player.bucketFull = false;
                    scene.networkClient.sendToolAction('bucket_pour', player.x, player.y, player.facing, player.isCrouching);
                    return true;
                }
            } else {
                // Fill bucket from water in front
                const frontTile = scene.getTile(frontX, feetY);
                if (frontTile === TileType.WATER || frontTile === TileType.WATER_SURFACE) {
                    scene.setLocalTile(frontX, feetY, TileType.SKY);
                    player.bucketFull = true;
                    scene.networkClient.sendToolAction('bucket_fill', player.x, player.y, player.facing, player.isCrouching);
                    return true;
                }
            }
            return false;
        }

        case TileType.ILO_KILI: { // Harvest basket
            const frontTile = scene.getTile(frontX, feetY);
            if (isFruitTile(frontTile) && (player.basketContents?.length ?? 0) < BASKET_CAPACITY) {
                // Collect fruit
                scene.setLocalTile(frontX, feetY, TileType.SKY);
                if (!player.basketContents) player.basketContents = [];
                player.basketContents.push(frontTile);
                scene.networkClient.sendToolAction('basket_collect', player.x, player.y, player.facing, player.isCrouching);
                return true;
            } else if (frontTile === TileType.SKY && player.basketContents?.length > 0) {
                // Place last fruit
                const fruit = player.basketContents.pop();
                scene.setLocalTile(frontX, feetY, fruit);
                scene.networkClient.sendToolAction('basket_place', player.x, player.y, player.facing, player.isCrouching);
                return true;
            }
            return false;
        }

        default:
            return false;
    }
}
